// Get This from the API
const CustomerAddressesPathSettings = {
    "Address1": {
        "label": "Addresses - Address1",
        "description": "The first line of the address. Typically the street address or PO Box number.",
        "dataType": "String",
        "optional": true
    },
    "Address2": {
        "label": "Addresses - Address2",
        "description": "The second line of the address. Typically the number of the apartment, suite, or unit.",
        "dataType": "String",
        "optional": true
    },
    "City": {
        "label": "Addresses - City",
        "description": "The name of the city, district, village, or town.",
        "dataType": "String",
        "optional": true
    },
    "Company": {
        "label": "Addresses - Company",
        "description": "The name of the customer's company or organization.",
        "dataType": "String",
        "optional": true
    },
    "Country": {
        "label": "Addresses - Country",
        "description": "The name of the country.",
        "dataType": "String",
        "optional": true
    },
    "CountryCodeV2": {
        "label": "Addresses - CountryCodeV2",
        "description": "The two-letter code for the country of the address. For example, US.",
        "dataType": "String",
        "optional": true
    },
    "FirstName": {
        "label": "Addresses - FirstName",
        "description": "The first name of the customer.",
        "dataType": "String",
        "optional": true
    },
    "LastName": {
        "label": "Addresses - LastName",
        "description": "The longitude coordinate of the customer address.",
        "dataType": "String",
        "optional": true
    },
    "Phone": {
        "label": "Addresses - Phone",
        "description": "A unique phone number for the customer. Formatted using E.164 standard. For example, +16135551111.",
        "dataType": "String",
        "optional": true
    },
    "Province": {
        "label": "Addresses - Province",
        "description": "The region of the address, such as the province, state, or district.",
        "dataType": "String",
        "optional": true
    },
    "ProvinceCode": {
        "label": "Addresses - ProvinceCode",
        "description": "The two-letter code for the region. For example, ON.",
        "dataType": "String",
        "optional": true
    },
    "Zip": {
        "label": "Addresses - Zip",
        "description": "The zip or postal code of the address.",
        "dataType": "String",
        "optional": true
    },
};

export const ProductAttributePathSettings = {
    "Title": {
        "label": "Title",
        "description": "The title of the product.",
        "dataType": "String",
        "optional": false
    },
    "Description": {
        "label": "Description",
        "description": "A stripped description of the product, single line with HTML tags removed.",
        "dataType": "String",
        "optional": false
    },
    "CreatedAt": {
        "label": "Created at",
        "description": "The date and time when the product was created.",
        "dataType": "DateTimeOffset",
        "optional": false
    },
    "UpdatedAt": {
        "label": "Updated at",
        "description": "The date and time when the product was last updated.",
        "dataType": "DateTimeOffset",
        "optional": false
    },
    "ProductType": {
        "label": "Product type",
        "description": "The product type specified by the merchant.",
        "dataType": "String",
        "optional": false
    },
    "Status": {
        "label": "Status",
        "description": "The product status. This controls visibility across all channels.",
        "dataType": "String",
        "allowedValues": [
            { label: 'ACTIVE', value: "ACTIVE", description: "The product is ready to sell and is available to customers on the online store, sales channels, and apps. By default, existing products are set to active." },
            { label: 'ARCHIVED', value: "ARCHIVED", description: "The product is no longer being sold and isn't available to customers on sales channels and apps." },
            { label: 'DRAFT', value: "DRAFT", description: "The product isn't ready to sell and is unavailable to customers on sales channels and apps. By default, duplicated and unarchived products are set to draft." },
        ],
        "optional": false
    },
    "Tags": {
        "label": "Tags",
        "description": "A list of tags associated with the product.",
        "dataType": "StringCollection",
        "optional": false
    },
    "Vendor": {
        "label": "Vendor",
        "description": "The name of the product's vendor.",
        "dataType": "String",
        "optional": false
    },
}

export const AttributePathSettings = {
    "OrdersCount": {
        "label": "Orders Count",
        "description": "The number of orders that the customer has made at the store in their lifetime.",
        "dataType": "int",
        "optional": false
    },
    "AcceptsMarketing": {
        "label": "Accepts marketing",
        "description": "Whether the customer has agreed to receive marketing material.",
        "dataType": "Boolean",
        "optional": false
    },
    "AcceptsMarketingUpdatedAt": {
        "label": "Accepts marketing updated at",
        "description": "The date and time when the customer consented or objected to receiving marketing material by email.",
        "dataType": "DateTimeOffset",
        "optional": false
    },
    "DefaultAddress": {
        "label": "Default address",
        "description": "The default address associated with the customer.",
        "dataType": "Custom",
        "optional": true
    },
    "DefaultAddress.City": {
        "label": "DefaultAddress - City",
        "description": "The name of the city, district, village, or town.",
        "dataType": "String",
        "optional": true
    },
    "DefaultAddress.Country": {
        "label": "DefaultAddress - Country",
        "description": "The name of the country.",
        "dataType": "String",
        "optional": true
    },
    "DefaultAddress.CountryCodeV2": {
        "label": "DefaultAddress - CountryCodeV2",
        "description": "The two-letter code for the country of the address. For example, US.",
        "dataType": "String",
        "optional": true
    },
    "DefaultAddress.Province": {
        "label": "DefaultAddress - Province",
        "description": "The region of the address, such as the province, state, or district.",
        "dataType": "String",
        "optional": true
    },
    "DefaultAddress.ProvinceCode": {
        "label": "DefaultAddress - ProvinceCode",
        "description": "The two-letter code for the region. For example, ON.",
        "dataType": "String",
        "optional": true
    },
    "DefaultAddress.Zip": {
        "label": "DefaultAddress - Zip",
        "description": "The zip or postal code of the address.",
        "dataType": "String",
        "optional": true
    },
    "AverageOrderAmountV2.Amount": {
        "label": "Average order amount",
        "description": "The average amount that the customer spent per order.",
        "dataType": "Double",
        "optional": true
    },
    "CreatedAt": {
        "label": "Created at",
        "description": "The date and time when the customer was added to the store.",
        "dataType": "DateTimeOffset",
        "optional": false
    },
    "MarketingOptInLevel": {
        "label": "Marketing opt-in level",
        "description": "The marketing subscription opt-in level, as described by the M3AAWG best practices guidelines, that the customer gave when they consented to receive marketing material by email.",
        "dataType": "Enum",
        "optional": true,
        "allowedValues": [
            { label: 'CONFIRMED_OPT_IN', value: "CONFIRMED_OPT_IN", description: "After providing their information, the customer receives a confirmation and is required to perform a intermediate step before receiving marketing information." },
            { label: 'SINGLE_OPT_IN', value: "SINGLE_OPT_IN", description: "After providing their information, the customer receives marketing information without any intermediate steps." },
            { label: 'UNKNOWN', value: "UNKNOWN", description: "The customer receives marketing information but how they were opted in is unknown." }
        ]
    },
    "SmsMarketingConsent.MarketingOptInLevel": {
        "label": "SMS marketing consent opt-int level",
        "description": "The marketing subscription opt in level set when the customer consented to receive marketing information over SMS.",
        "dataType": "Enum",
        "optional": true,
        "allowedValues": [
            { label: 'CONFIRMED_OPT_IN', value: "CONFIRMED_OPT_IN", description: "After providing their information, the customer receives a confirmation and is required to perform a intermediate step before receiving marketing information." },
            { label: 'SINGLE_OPT_IN', value: "SINGLE_OPT_IN", description: "After providing their information, the customer receives marketing information without any intermediate steps." },
            { label: 'UNKNOWN', value: "UNKNOWN", description: "The customer receives marketing information but how they were opted in is unknown." }
        ]
    },
    "SmsMarketingConsent.MarketingState": {
        "label": "SMS marketing consent marketing state",
        "description": "The current SMS marketing state for the customer.",
        "dataType": "Enum",
        "optional": true,
        "allowedValues": [
            { label: 'NOT_SUBSCRIBED', value: "NOT_SUBSCRIBED", description: "The customer hasn't subscribed to SMS marketing." },
            { label: 'PENDING', value: "PENDING", description: "The customer is in the process of subscribing to SMS marketing." },
            { label: 'REDACTED', value: "REDACTED", description: "The customer's personal data is erased. This value is internally-set and read-only." },
            { label: 'SUBSCRIBED', value: "SUBSCRIBED", description: "The customer is subscribed to SMS marketing." },
            { label: 'UNSUBSCRIBED', value: "UNSUBSCRIBED", description: "The customer isn't currently subscribed to SMS marketing but was previously subscribed." },
        ]
    },
    "State": {
        "label": "Customer state",
        "description": "The state of the customer's account with the shop.",
        "dataType": "Enum",
        "optional": false,
        "allowedValues": [
            { label: 'DECLINED', value: "DECLINED", description: "The customer declined the email invite to create an account." },
            { label: 'DISABLED', value: "DISABLED", description: "The customer doesn't have an active account. Customer accounts can be disabled from the Shopify admin at any time." },
            { label: 'ENABLED', value: "ENABLED", description: "The customer has created an account." },
            { label: 'INVITED', value: "INVITED", description: "The customer has received an email invite to create an account." },
        ]
    },
    "Tags": {
        "label": "Customer tags",
        "description": "Tags that have been added to the customer.",
        "dataType": "StringCollection",
        "optional": false,
    },
    "TaxExempt": {
        "label": "Tax exempt",
        "description": "Whether the customer is exempt from being charged taxes on their orders.",
        "dataType": "Boolean",
        "optional": false,
    },
    "TotalSpentV2.Amount": {
        "label": "Total spent",
        "description": "The total amount that the customer has spent on orders in their lifetime.",
        "dataType": "Double",
        "optional": false,
    },
    "UpdatedAt": {
        "label": "Updated at",
        "description": "The date and time when the customer information was updated.",
        "dataType": "DateTimeOffset",
        "optional": false
    },
    "VerifiedEmail": {
        "label": "Verified email",
        "description": "Whether the customer has verified their email address.",
        "dataType": "Boolean",
        "optional": false,
    },
};

export const OrderAttributePathSettings = {
    "Name": {
        "label": "Order name",
        "description": "The the order's name.",
        "dataType": "String",
        "optional": false
    },
    "CreatedAt": {
        "label": "Created at",
        "description": "The date and time when the order was created.",
        "dataType": "DateTimeOffset",
        "optional": false
    },
    "Tags": {
        "label": "Order tags",
        "description": "Tags that have been added to the order.",
        "dataType": "StringCollection",
        "optional": false,
    },
    "UpdatedAt": {
        "label": "Updated at",
        "description": "The date and time when the order information was updated.",
        "dataType": "DateTimeOffset",
        "optional": false
    },
};

const defaultBody = {
    "filterType": "AttributeFilter",
    "attributePath": null,
    "conditionOperator": "EqualTo",
    "conditionValue": null,
    "conditionType": "Comparable"
};

export class UIAttributeFilter {

    constructor(id, body, pathSettings) {

        this.pathSettings = pathSettings

        if (this.pathSettings == null) {
            this.pathSettings = AttributePathSettings;
        }

        this.id = id;
        this.attributeIsOptional = this.pathSettings[body.attributePath].optional;
        this.attributeType = this.pathSettings[body.attributePath].dataType;
        this.allowedValues = this.pathSettings[body.attributePath].allowedValues;
        // we expect to have a valid body here
        this.body = body;

        this.validateInternal();
    }

    static fromBody(id, body, pathSettings) {
        return new UIAttributeFilter(id, body, pathSettings);
    }

    static fromAttributePath(id, attributePath, pathSettings) {

        if (pathSettings == null) {
            pathSettings = AttributePathSettings;
        }

        var attributeSettings = pathSettings[attributePath];
        var filterSettings = DataTypeFilterSettings[attributeSettings.dataType];
        var optionalFilterSettings = OptionalDataTypeFilterSettings;
        var defaultOperator = "EqualTo";
        var defaultSettings = filterSettings[defaultOperator];

        if (defaultSettings == null) {
            defaultOperator = "HasValue"
            defaultSettings = optionalFilterSettings[defaultOperator];
        }

        var copiedBody = JSON.parse(JSON.stringify(defaultBody));

        var body = {
            ...copiedBody,
            "attributePath": attributePath,
            "conditionOperator": defaultOperator,
            "conditionValue": null,
            "conditionType": defaultSettings.conditionType
        };

        return new UIAttributeFilter(id, body, pathSettings);

    }

    updateConditionOperator(newConditionOperator) {
        var attributeSettings = this.pathSettings[this.body.attributePath];
        var filterTypeSettings = getConditionTypeSettings(attributeSettings.dataType, attributeSettings.optional);
        var currentOperatorSettings = filterTypeSettings[this.body.conditionOperator];
        var newOperatorSettings = filterTypeSettings[newConditionOperator];
        const conditionTypeChanged = currentOperatorSettings.conditionType !== newOperatorSettings.conditionType;

        if (conditionTypeChanged) {
            this.body.conditionValue = null;
            this.body.conditionType = newOperatorSettings.conditionType;
        }

        // if (newConditionOperator === "HasValue" || newConditionOperator === "HasNoValue") {
        //     this.body.conditionValue = newConditionOperator === "HasValue" ? true : false;
        // }

        this.body.conditionOperator = newConditionOperator;
        this.validateInternal();
    }

    updateConditionValue(newConditionValue) {
        this.body.conditionValue = newConditionValue;
        this.validateInternal();
    }

    getLabel() {
        return this.pathSettings[this.body.attributePath].label;
    }

    validateInternal() {
        this.error = this.validate(this.body);
    }

    validate(body) {

        if (body == null) {
            return "Filter is null";
        }

        if (body.filterType !== "AttributeFilter") {
            return "Filter is not AttributeFilter";
        }

        if (body.attributePath == null) {
            return "Attribute path must be specified";
        }

        if (body.conditionOperator == null) {
            return "Operator must be specified";
        }

        if (body.conditionType == null) {
            return "Condition value must be specified";
        }

        if (body.conditionType === "HasValue") {
            return null;
        }

        if (body.conditionValue == null || body.conditionValue === "") {
            return "Condition value must be specified";
        }

        return null;
    }
}

export class UICustomerAddressesFilter {

    constructor(id, body) {
        this.id = id;
        // this.attributeIsOptional = AttributePathSettings[body.attributePath].optional;
        // this.attributeType = AttributePathSettings[body.attributePath].dataType;
        // we expect to have a valid body here
        this.body = body;
        this.itemsFilter = UIAttributeFilter.fromBody(id, this.body.itemsFilter, CustomerAddressesPathSettings);

        this.validateInternal();
    }

    static fromBody(id, body) {
        return new UICustomerAddressesFilter(id, body);
    }

    static fromAttributePath(id, attributePath) {

        this.itemsFilter = UIAttributeFilter.fromAttributePath(id, attributePath, CustomerAddressesPathSettings);
        let body = {
            "filterType": "CustomerAddressesFilter",
            "requirement": "Any",
            "itemsFilter": this.itemsFilter.body
        };

        return new UICustomerAddressesFilter(id, body);
    }

    updateConditionOperator(newConditionOperator) {
        this.itemsFilter.updateConditionOperator(newConditionOperator);
        this.body.itemsFilter = this.itemsFilter.body;
        this.validateInternal();
    }

    updateConditionValue(newConditionValue) {
        this.itemsFilter.updateConditionValue(newConditionValue);
        this.body.itemsFilter = this.itemsFilter.body;
        this.validateInternal();
    }

    updateRequirement(newValue) {
        this.body.requirement = newValue
        this.validateInternal();
    }

    getLabel() {
        return CustomerAddressesPathSettings[this.itemsFilter.body.attributePath].label;
    }

    validateInternal() {
        this.error = this.validate(this.body);
    }

    validate(body) {

        if (body == null) {
            return "Filter is null";
        }

        if (body.filterType !== "CustomerAddressesFilter") {
            return "Filter is not CustomerAddressesFilter";
        }

        if (body.requirement == null) {
            return "Requirement path must be specified";
        }

        return this.itemsFilter.validate(body.itemsFilter);
    }
}


export class UICustomerFilterDescriptor {

    constructor(filterIdentifier, title, description, category, factory) {
        this.filterIdentifier = filterIdentifier;
        this.title = title;
        this.description = description;
        this.category = category;
        this.factory = factory;
    }

    createFilter = (id) => {
        return this.factory(id);
    }
}

export const AvailableProductFilters = [
    new UICustomerFilterDescriptor(
        "ProductTitle",
        "Title",
        "Filter product based on title",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "Title", ProductAttributePathSettings)
    ),
    new UICustomerFilterDescriptor(
        "ProductDescription",
        "Description",
        "A stripped description of the product, single line with HTML tags removed.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "Description", ProductAttributePathSettings)
    ),
    new UICustomerFilterDescriptor(
        "ProductCreatedAt",
        "CreatedAt",
        "The date and time when the product was created.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "CreatedAt", ProductAttributePathSettings)
    ),
    new UICustomerFilterDescriptor(
        "ProductUpdatedAt",
        "UpdatedAt",
        "The date and time when the product was last updated.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "UpdatedAt", ProductAttributePathSettings)
    ),
    new UICustomerFilterDescriptor(
        "ProductProductType",
        "ProductType",
        "The product type specified by the merchant.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "ProductType", ProductAttributePathSettings)
    ),
    new UICustomerFilterDescriptor(
        "ProductStatus",
        "Status",
        "The product status. This controls visibility across all channels.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "Status", ProductAttributePathSettings)
    ),
    new UICustomerFilterDescriptor(
        "ProductTags",
        "Tags",
        "A list of tags associated with the product.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "Tags", ProductAttributePathSettings)
    ),
    new UICustomerFilterDescriptor(
        "ProductVendor",
        "Vendor",
        "The name of the product's vendor.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "Vendor", ProductAttributePathSettings)
    ),
];

export const AvailableOrderFilters = [
    new UICustomerFilterDescriptor(
        "OrderName",
        "Name",
        "Filter orders based on name",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "Name", OrderAttributePathSettings)
    ),
    new UICustomerFilterDescriptor(
        "OrderCreatedAt",
        "CreatedAt",
        "The date and time when the order was created.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "CreatedAt", OrderAttributePathSettings)
    ),
    new UICustomerFilterDescriptor(
        "OrderUpdatedAt",
        "UpdatedAt",
        "The date and time when the order was last updated.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "UpdatedAt", OrderAttributePathSettings)
    ),
    new UICustomerFilterDescriptor(
        "OrderTags",
        "Tags",
        "A list of tags associated with the order.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "Tags", OrderAttributePathSettings)
    ),
];

export const AvailableCustomerFilters = [
    new UICustomerFilterDescriptor(
        "CustomerOrdersCount",
        "Orders count",
        "Filter customers based on their order count.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "OrdersCount")),
    new UICustomerFilterDescriptor(
        "CustomerAcceptsMarketing",
        "Accepts marketing",
        "Whether the customer has agreed to receive marketing material.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "AcceptsMarketing")),
    new UICustomerFilterDescriptor(
        "CustomerAcceptsMarketingUpdatedAt",
        "Accepts marketing updated at",
        "The date and time when the customer consented or objected to receiving marketing material by email.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "AcceptsMarketingUpdatedAt")),
    new UICustomerFilterDescriptor(
        "CustomerDefaultAddress",
        "Default address",
        "The default address associated with the customer.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "DefaultAddress")),

    new UICustomerFilterDescriptor(
        "CustomerAddressesCity",
        "City",
        "The name of the city, district, village, or town.",
        "Addresses",
        (id) => UICustomerAddressesFilter.fromAttributePath(id, "City")),
    new UICustomerFilterDescriptor(
        "CustomerAddressesCountry",
        "Country",
        "The name of the country.",
        "Addresses",
        (id) => UICustomerAddressesFilter.fromAttributePath(id, "Country")),
    new UICustomerFilterDescriptor(
        "CustomerAddressesCountryCodeV2",
        "CountryCodeV2",
        "The two-letter code for the country of the address.",
        "Addresses",
        (id) => UICustomerAddressesFilter.fromAttributePath(id, "CountryCodeV2")),
    new UICustomerFilterDescriptor(
        "CustomerAddressesProvince",
        "Province",
        "The region of the address, such as the province, state, or district.",
        "Addresses",
        (id) => UICustomerAddressesFilter.fromAttributePath(id, "Province")),
    new UICustomerFilterDescriptor(
        "CustomerAddressesProvinceCode",
        "ProvinceCode",
        "The two-letter code for the region.",
        "Addresses",
        (id) => UICustomerAddressesFilter.fromAttributePath(id, "ProvinceCode")),
    new UICustomerFilterDescriptor(
        "CustomerAddressesZip",
        "Zip",
        "The zip or postal code of the address.",
        "Addresses",
        (id) => UICustomerAddressesFilter.fromAttributePath(id, "Zip")),

    new UICustomerFilterDescriptor(
        "CustomerDefaultAddressCity",
        "City",
        "The name of the city, district, village, or town.",
        "Default address",
        (id) => UIAttributeFilter.fromAttributePath(id, "DefaultAddress.City")),
    new UICustomerFilterDescriptor(
        "CustomerDefaultAddressCountry",
        "Country",
        "The name of the country.",
        "Default address",
        (id) => UIAttributeFilter.fromAttributePath(id, "DefaultAddress.Country")),
    new UICustomerFilterDescriptor(
        "CustomerDefaultAddressCountryCodeV2",
        "CountryCodeV2",
        "The two-letter code for the country of the address.",
        "Default address",
        (id) => UIAttributeFilter.fromAttributePath(id, "DefaultAddress.CountryCodeV2")),
    new UICustomerFilterDescriptor(
        "CustomerDefaultAddressProvince",
        "Province",
        "The region of the address, such as the province, state, or district.",
        "Default address",
        (id) => UIAttributeFilter.fromAttributePath(id, "DefaultAddress.Province")),
    new UICustomerFilterDescriptor(
        "CustomerDefaultAddressProvinceCode",
        "ProvinceCode",
        "The two-letter code for the region.",
        "Default address",
        (id) => UIAttributeFilter.fromAttributePath(id, "DefaultAddress.ProvinceCode")),
    new UICustomerFilterDescriptor(
        "CustomerDefaultAddressZip",
        "Zip",
        "The zip or postal code of the address.",
        "Default address",
        (id) => UIAttributeFilter.fromAttributePath(id, "DefaultAddress.Zip")),

    // new UICustomerFilterDescriptor(
    //     "CustomerAverageOrderAmountV2.Amount",
    //     "Average order amount",
    //     "The average amount that the customer spent per order.",
    //     "Attribute Filters",
    //     (id) => UIAttributeFilter.fromAttributePath(id, "AverageOrderAmountV2.Amount")),
    new UICustomerFilterDescriptor(
        "CustomerCreatedAt",
        "Created at",
        "The date and time when the customer was added to the store.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "CreatedAt")),
    // new UICustomerFilterDescriptor(
    //     "Locale",
    //     "Locale",
    //     "The customer's locale.",
    //     "Attribute Filters",
    //     (id) => UIAttributeFilter.fromAttributePath(id, "Locale")),
    new UICustomerFilterDescriptor(
        "MarketingOptInLevel",
        "Marketing opt-in level",
        "The marketing subscription opt-in level that the customer gave when they consented to receive marketing material by email.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "MarketingOptInLevel")),
    new UICustomerFilterDescriptor(
        "CustomerOrdersCount",
        "Created at",
        "The number of orders that the customer has made at the store in their lifetime.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "OrdersCount")),
    new UICustomerFilterDescriptor(
        "CustomerSmsMarketingConsent.MarketingOptInLevel",
        "SMS marketing consent opt-int level",
        "The marketing subscription opt in level set when the customer consented to receive marketing information over SMS.",
        "SMS Marketing",
        (id) => UIAttributeFilter.fromAttributePath(id, "SmsMarketingConsent.MarketingOptInLevel")),
    new UICustomerFilterDescriptor(
        "CustomerSmsMarketingConsent.MarketingState",
        "SMS marketing consent marketing state",
        "The current SMS marketing state for the customer.",
        "SMS Marketing",
        (id) => UIAttributeFilter.fromAttributePath(id, "SmsMarketingConsent.MarketingState")),
    new UICustomerFilterDescriptor(
        "CustomerState",
        "Customer state",
        "The state of the customer's account with the shop.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "State")),
    new UICustomerFilterDescriptor(
        "CustomerTags",
        "Customer tags",
        "Tags that have been added to the customer.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "Tags")),
    new UICustomerFilterDescriptor(
        "CustomerTaxExempt",
        "Tax exempt",
        "Whether the customer is exempt from being charged taxes on their orders.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "TaxExempt")),
    new UICustomerFilterDescriptor(
        "CustomerTotalSpentV2.Amount",
        "Total spent",
        "The total amount that the customer has spent on orders in their lifetime.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "TotalSpentV2.Amount")),
    new UICustomerFilterDescriptor(
        "CustomerUpdatedAt",
        "Updated at",
        "The date and time when the customer information was updated.",
        "Attribute Filters",
        (id) => UIAttributeFilter.fromAttributePath(id, "UpdatedAt")),
    new UICustomerFilterDescriptor(
        "CustomerVerifiedEmail",
        "Verified email",
        "Whether the customer has verified their email address",
        "Email",
        (id) => UIAttributeFilter.fromAttributePath(id, "VerifiedEmail")),
];

export const DataTypeFilterSettings = {
    "String": {
        "EqualTo": { label: 'is equal to', conditionType: "String" },
        "NotEqualTo": { label: 'is not equal to', conditionType: "String" },
        "Contains": { label: 'contains', conditionType: "String" },
        "DoesNotContain": { label: 'does not contain', conditionType: "String" },
        "StartsWith": { label: 'starts with', conditionType: "String" },
        "DoesNotStartWith": { label: 'does not start with', conditionType: "String" },
        "EndsWith": { label: 'ends with', conditionType: "String" },
        "DoesNotEndWith": { label: 'does not end with', conditionType: "String" },
    },
    "int": {
        "EqualTo": { label: 'is equal to', conditionType: "Comparable" },
        "NotEqualTo": { label: 'is not equal to', conditionType: "Comparable" },
        "GreaterThan": { label: 'is greater than', conditionType: "Comparable" },
        "GreaterThanOrEqual": { label: 'is greater than or equal', conditionType: "Comparable" },
        "LessThanOrEqual": { label: 'is less than or equal to', conditionType: "Comparable" },
        "LessThan": { label: 'is less than', conditionType: "Comparable" },
    },
    "Double": {
        "EqualTo": { label: 'is equal to', conditionType: "Comparable" },
        "NotEqualTo": { label: 'is not equal to', conditionType: "Comparable" },
        "GreaterThan": { label: 'is greater than', conditionType: "Comparable" },
        "GreaterThanOrEqual": { label: 'is greater than or equal', conditionType: "Comparable" },
        "LessThanOrEqual": { label: 'is less than or equal to', conditionType: "Comparable" },
        "LessThan": { label: 'is less than', conditionType: "Comparable" },
    },
    "Boolean": {
        "EqualTo": { label: 'is equal to', conditionType: "Comparable" },
        "NotEqualTo": { label: 'is not equal to', conditionType: "Comparable" },
    },
    "DateTimeOffset": {
        "EqualTo": { label: 'is', conditionType: "Comparable" },
        "NotEqualTo": { label: 'is not', conditionType: "Comparable" },
        "GreaterThan": { label: 'is after', conditionType: "Comparable" },
        "LessThan": { label: 'is before', conditionType: "Comparable" },
        "AtLeast": { label: 'at least', conditionType: "DateOffset" },
        "AtMost": { label: 'at most', conditionType: "DateOffset" },
    },
    "Custom": {
    },
    "Enum": {
        "EqualTo": { label: 'is equal to', conditionType: "Enum" },
        "NotEqualTo": { label: 'is not equal to', conditionType: "Enum" },
    },
    "StringCollection": {
        "EqualTo": { label: 'contains tag ', conditionType: "StringCollection" },
        "NotEqualTo": { label: 'does not have tag', conditionType: "StringCollection" },
        "Contains": { label: 'has tags containing', conditionType: "StringCollection" },
        "DoesNotContain": { label: 'does not have tags containing', conditionType: "StringCollection" },
        "StartsWith": { label: 'has tags starting with', conditionType: "StringCollection" },
        "DoesNotStartWith": { label: 'does not have tags starting with', conditionType: "StringCollection" },
        "EndsWith": { label: 'has tags ending with', conditionType: "StringCollection" },
        "DoesNotEndWith": { label: 'does not have tags ending with', conditionType: "StringCollection" },
    },
};

export const OptionalDataTypeFilterSettings =
{
    "HasValue": { label: 'has value', conditionType: "HasValue" },
    "HasNoValue": { label: 'has no value', conditionType: "HasValue" },
};

export const CustomerAddressesRequirementOptions =
    [
        { label: 'of all items', value: "All" },
        { label: 'of at least 1 item', value: "Any" },
        { label: 'of no items', value: "None" },
    ];

export function conditionTypeSettingsToSelectOptions(attributeType, isOptional) {
    var settings = DataTypeFilterSettings[attributeType];
    var typeOptions = Object.keys(settings).map(_ => {
        return {
            label: settings[_].label,
            value: _,
        }
    });

    var optionalTypeOptions = [];

    if (isOptional) {
        var optionalTypeSettings = OptionalDataTypeFilterSettings;
        optionalTypeOptions = Object.keys(optionalTypeSettings).map(_ => {
            return {
                label: optionalTypeSettings[_].label,
                value: _,
            }
        });
    }

    return [
        ...typeOptions,
        ...optionalTypeOptions
    ];
}

export function getConditionTypeSettings(attributeType, isOptional) {
    var settings = DataTypeFilterSettings[attributeType];
    var optionalSettings = {};

    if (isOptional) {
        optionalSettings = OptionalDataTypeFilterSettings;
    }

    return {
        ...settings,
        ...optionalSettings
    };
}